import { useState, useMemo, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { ArrowRight, ArrowLeft, AlertTriangle, Check, Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DECISION_TRACKS } from '@/lib/decisionTracks';

// ── Types ──

export interface ContextHints {
  themeHint?: string | null;
  goalHint?: string | null;
  timeHorizonHint?: string | null;
  recognizedOptionFragment?: string | null;
  constraintHint?: string | null;
}

export interface MissingFieldQuestion {
  fieldName: string;
  priority: 1 | 2 | 3;
  question: string;
  helpText?: string;
  inputType: 'text' | 'textarea' | 'slider' | 'select' | 'dual_text';
  options?: Array<{ value: string; label: string; description?: string }>;
  contextHint?: string | null;
  prefillA?: string;
  prefillB?: string;
  prefillValue?: string | number | null;
}

interface MissingFieldResolutionProps {
  missingFields: string[];
  extractedContext: Record<string, unknown>;
  contextHints: ContextHints;
  onResolve: (fieldKey: string, value: string | number) => void;
  onComplete: () => void;
  onBack: () => void;
  onFieldShown?: (fieldName: string) => void;
  onFieldResolved?: (fieldName: string) => void;
  onValidationFailed?: (fieldName: string) => void;
  isPoorExtraction?: boolean;
}

// ── Track options ──

const TRACK_OPTIONS = DECISION_TRACKS.map(t => ({
  value: t.id, label: t.label, description: t.description,
}));

const PREFERENCE_OPTIONS = [
  { value: 'a', label: 'Richtung A', description: 'Tendenz zur ersten Option' },
  { value: 'b', label: 'Richtung B', description: 'Tendenz zur zweiten Option' },
  { value: 'undecided', label: 'Noch unklar', description: 'Keine klare Tendenz' },
];

// ── Question builder ──

function buildMissingFieldQuestions(
  missingFields: string[],
  context: ContextHints,
  extractedContext: Record<string, unknown>,
): MissingFieldQuestion[] {
  const questions: MissingFieldQuestion[] = [];
  const needsA = missingFields.includes('optionA');
  const needsB = missingFields.includes('optionB');

  const existingA = extractedContext?.optionA ? String(extractedContext.optionA) : '';
  const existingB = extractedContext?.optionB ? String(extractedContext.optionB) : '';

  if (needsA || needsB) {
    questions.push({
      fieldName: needsA && needsB ? 'optionPair' : (needsA ? 'optionA' : 'optionB'),
      priority: 1,
      question: context.themeHint
        ? `Wir sehen ein Spannungsfeld rund um ${context.themeHint}. Bestätige oder korrigiere die zwei Richtungen.`
        : 'Bestätige oder korrigiere die zwei Handlungsrichtungen.',
      helpText: 'Erkannt aus deinem Input. Passe nur an, wenn es noch nicht sauber trifft.',
      inputType: needsA && needsB ? 'dual_text' : 'textarea',
      contextHint: context.recognizedOptionFragment,
      prefillA: existingA,
      prefillB: existingB,
      prefillValue: needsA && !needsB ? existingA : (!needsA && needsB ? existingB : null),
    });
  }

  if (missingFields.includes('decisionType')) {
    questions.push({
      fieldName: 'decisionType',
      priority: 1,
      question: 'Wie würdest du diese Entscheidung einordnen?',
      helpText: 'Die Einordnung bestimmt die Analyseperspektive des Systems.',
      inputType: 'select',
      options: TRACK_OPTIONS,
      prefillValue: extractedContext?.decisionType ? String(extractedContext.decisionType) : null,
    });
  }

  if (missingFields.includes('impactScore')) {
    questions.push({
      fieldName: 'impactScore',
      priority: 2,
      question: context.constraintHint
        ? `Wenn sich an ${context.constraintHint} nichts ändert: Wie kritisch ist diese Entscheidung wirklich?`
        : 'Wie kritisch ist diese Entscheidung wirklich?',
      inputType: 'slider',
      prefillValue: typeof extractedContext?.impactScore === 'number' ? extractedContext.impactScore : null,
    });
  }

  if (missingFields.includes('urgencyScore')) {
    questions.push({
      fieldName: 'urgencyScore',
      priority: 2,
      question: context.goalHint
        ? `Um ${context.goalHint} zu erreichen: Wie dringend muss diese Entscheidung fallen?`
        : 'Wie dringend muss diese Entscheidung fallen?',
      inputType: 'slider',
      prefillValue: typeof extractedContext?.urgencyScore === 'number' ? extractedContext.urgencyScore : null,
    });
  }

  if (missingFields.includes('initialPreference')) {
    questions.push({
      fieldName: 'initialPreference',
      priority: 3,
      question: 'Wenn du dich jetzt festlegen müsstest: Wohin tendierst du?',
      inputType: 'select',
      options: PREFERENCE_OPTIONS,
      prefillValue: extractedContext?.initialPreference ? String(extractedContext.initialPreference) : null,
    });
  }

  return questions.sort((a, b) => a.priority - b.priority).slice(0, 3);
}

// ── Validation ──

function validateAnswer(fieldName: string, value: unknown): string | null {
  const str = String(value ?? '').trim();
  if (fieldName === 'optionPair') return null;
  if (fieldName === 'optionA' || fieldName === 'optionB') {
    if (str.length < 10) return 'Bitte mindestens 10 Zeichen eingeben.';
    return null;
  }
  if (fieldName === 'decisionType') return str ? null : 'Bitte wähle einen Bereich aus.';
  if (fieldName === 'impactScore' || fieldName === 'urgencyScore') {
    const n = Number(value);
    if (isNaN(n) || n < 1 || n > 10) return 'Wert muss zwischen 1 und 10 liegen.';
    return null;
  }
  return null;
}

function getCtaLabel(fieldName: string): string {
  if (fieldName === 'optionPair' || fieldName === 'optionA' || fieldName === 'optionB') return 'Richtungen bestätigen';
  if (fieldName === 'decisionType') return 'Einordnung bestätigen';
  if (fieldName === 'impactScore') return 'Impact festlegen';
  if (fieldName === 'urgencyScore') return 'Dringlichkeit festlegen';
  if (fieldName === 'initialPreference') return 'Tendenz bestätigen';
  return 'Bestätigen';
}

function getImpactLabel(score: number): string {
  if (score <= 2) return 'Kaum spürbar';
  if (score <= 4) return 'Hat begrenzten Einfluss';
  if (score <= 6) return 'Beeinflusst den Erfolg spürbar';
  if (score <= 8) return 'Entscheidend für das Ergebnis';
  return 'Existenziell. Kein Raum für Fehler.';
}

function getUrgencyLabel(score: number): string {
  if (score <= 2) return 'Kann warten';
  if (score <= 4) return 'Sollte bald geklärt werden';
  if (score <= 6) return 'Zeitnah entscheiden';
  if (score <= 8) return 'Drängt. Verzögerung kostet.';
  return 'Sofort. Jeder Tag zählt.';
}

// ── Text condensation ──

function truncateOption(text: string, maxLen = 80): string {
  if (text.length <= maxLen) return text;
  const cut = text.slice(0, maxLen).replace(/\s+\S*$/, '');
  return (cut || text.slice(0, maxLen)) + '…';
}

// ── Component ──

export const MissingFieldResolution = ({
  missingFields,
  extractedContext,
  contextHints,
  onResolve,
  onComplete,
  onBack,
  onFieldShown,
  onFieldResolved,
  onValidationFailed,
  isPoorExtraction,
}: MissingFieldResolutionProps) => {
  const initialMissingRef = useRef(missingFields);
  const questions = useMemo(
    () => buildMissingFieldQuestions(initialMissingRef.current, contextHints, extractedContext),
    [contextHints, extractedContext],
  );

  const [answers, setAnswers] = useState<Record<string, string | number>>({});
  const [dualText, setDualText] = useState<{ a: string; b: string }>({ a: '', b: '' });
  const [currentIdx, setCurrentIdx] = useState(0);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [completed, setCompleted] = useState(false);
  const shownFieldsRef = useRef<Set<string>>(new Set());
  const prefilledFieldsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const currentQ = questions[currentIdx];
    if (!currentQ || prefilledFieldsRef.current.has(currentQ.fieldName)) return;

    if (currentQ.inputType === 'dual_text') {
      const nextA = currentQ.prefillA || '';
      const nextB = currentQ.prefillB || '';
      if (nextA || nextB) {
        setDualText({ a: nextA, b: nextB });
      }
      prefilledFieldsRef.current.add(currentQ.fieldName);
      return;
    }

    if (currentQ.prefillValue !== null && currentQ.prefillValue !== undefined && currentQ.prefillValue !== '') {
      setAnswers(prev => ({ ...prev, [currentQ.fieldName]: currentQ.prefillValue as string | number }));
    }

    prefilledFieldsRef.current.add(currentQ.fieldName);
  }, [questions, currentIdx]);

  const optionA = extractedContext?.optionA ? String(extractedContext.optionA) : null;
  const optionB = extractedContext?.optionB ? String(extractedContext.optionB) : null;
  const hasRecognizedOptions = optionA || optionB;

  useEffect(() => {
    if (questions.length === 0 && !completed) {
      setCompleted(true);
      onComplete();
    }
  }, [questions.length, onComplete, completed]);

  const currentQ = questions[currentIdx];
  useEffect(() => {
    if (currentQ && !shownFieldsRef.current.has(currentQ.fieldName)) {
      shownFieldsRef.current.add(currentQ.fieldName);
      onFieldShown?.(currentQ.fieldName);
    }
  }, [currentQ, onFieldShown]);

  if (questions.length === 0 || completed || !currentQ) return null;

  const handleNext = (value?: string | number) => {
    const v = value ?? answers[currentQ.fieldName];

    if (currentQ.fieldName === 'optionPair') {
      if (dualText.a.trim().length < 10 || dualText.b.trim().length < 10) {
        setValidationError('Beide Richtungen müssen mindestens 10 Zeichen lang sein.');
        onValidationFailed?.(currentQ.fieldName);
        return;
      }
      if (dualText.a.trim() === dualText.b.trim()) {
        setValidationError('Die Richtungen müssen sich unterscheiden.');
        onValidationFailed?.(currentQ.fieldName);
        return;
      }
      onResolve('optionA', dualText.a.trim());
      onResolve('optionB', dualText.b.trim());
      onFieldResolved?.('optionA');
      onFieldResolved?.('optionB');
    } else {
      const err = validateAnswer(currentQ.fieldName, v);
      if (err) {
        setValidationError(err);
        onValidationFailed?.(currentQ.fieldName);
        return;
      }
      onResolve(currentQ.fieldName, v!);
      onFieldResolved?.(currentQ.fieldName);
    }

    setValidationError(null);
    if (currentIdx >= questions.length - 1) {
      setCompleted(true);
      onComplete();
    } else {
      setCurrentIdx(currentIdx + 1);
    }
  };

  const handleSelectAnswer = (value: string) => {
    setAnswers(prev => ({ ...prev, [currentQ.fieldName]: value }));
    setValidationError(null);
    const err = validateAnswer(currentQ.fieldName, value);
    if (err) {
      setValidationError(err);
      onValidationFailed?.(currentQ.fieldName);
      return;
    }
    onResolve(currentQ.fieldName, value);
    onFieldResolved?.(currentQ.fieldName);
    if (currentIdx >= questions.length - 1) {
      setCompleted(true);
      onComplete();
    } else {
      setCurrentIdx(currentIdx + 1);
    }
  };

  const remaining = questions.length - currentIdx;
  const isSingleQuestion = questions.length === 1;
  const currentSliderValue = Number(answers[currentQ.fieldName] ?? 5);

  return (
    <div className="max-w-2xl mx-auto space-y-8 py-4 animate-fade-in">
      {hasRecognizedOptions && currentQ.fieldName !== 'optionPair' && currentQ.fieldName !== 'optionA' && currentQ.fieldName !== 'optionB' && (
        <div className="rounded-lg bg-muted/10 border border-border/30 px-5 py-4 space-y-2">
          <p className="text-[10px] font-semibold text-muted-foreground/40 uppercase tracking-[0.18em]">Du entscheidest gerade zwischen</p>
          <div className="flex flex-col gap-1.5">
            {optionA && (
              <p className="text-sm text-foreground/80 flex items-center gap-2">
                <span className="text-primary">→</span> {truncateOption(optionA)}
              </p>
            )}
            {optionB && (
              <p className="text-sm text-foreground/80 flex items-center gap-2">
                <span className="text-primary">→</span> {truncateOption(optionB)}
              </p>
            )}
          </div>
          <p className="text-xs text-muted-foreground/50 pt-1">brauchen wir noch eine Einschätzung:</p>
        </div>
      )}

      <div>
        <h2 className="text-xl font-semibold tracking-tight text-foreground mb-1.5">
          {isPoorExtraction
            ? 'Wir brauchen deine Hilfe bei der Einordnung'
            : isSingleQuestion
              ? 'Ein Punkt ist noch unklar'
              : `Wir müssen ${remaining} Punkte klären`}
        </h2>
        <p className="text-sm text-muted-foreground/60">
          {isPoorExtraction
            ? 'Damit die Analyse belastbar wird, brauchen wir deine Bestätigung.'
            : 'Damit die Entscheidung belastbar ist, brauchen wir hier deine Bestätigung.'}
        </p>
        {questions.length > 1 && (
          <p className="text-xs text-muted-foreground/40 mt-2">Schritt {currentIdx + 1} von {questions.length}</p>
        )}
      </div>

      <div className="flex gap-1.5">
        {questions.map((_, i) => (
          <div
            key={i}
            className={cn(
              'flex-1 h-1 rounded-full transition-all duration-300',
              i < currentIdx ? 'bg-primary' : i === currentIdx ? 'bg-primary/60' : 'bg-border/60',
            )}
          />
        ))}
      </div>

      <div className="space-y-5">
        <p className="text-base font-medium text-foreground leading-relaxed">{currentQ.question}</p>
        {currentQ.helpText && (
          <p className="text-xs text-muted-foreground/50 flex items-center gap-1.5">
            <Pencil className="h-3 w-3 shrink-0" />
            {currentQ.helpText}
          </p>
        )}

        {currentQ.inputType === 'dual_text' && (
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground/60 mb-1.5 block">Richtung A</label>
              <textarea
                value={dualText.a}
                onChange={(e) => { setDualText(p => ({ ...p, a: e.target.value })); setValidationError(null); }}
                className="w-full rounded-lg border border-border/50 bg-background px-4 py-3 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary/20 min-h-[70px] resize-none"
                placeholder="Erste Handlungsrichtung…"
              />
              {dualText.a.trim().length >= 10 && (
                <p className="text-[10px] text-primary/60 mt-1 flex items-center gap-1">
                  <Check className="h-2.5 w-2.5" /> Vorschlag vorhanden
                </p>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground/60 mb-1.5 block">Richtung B</label>
              <textarea
                value={dualText.b}
                onChange={(e) => { setDualText(p => ({ ...p, b: e.target.value })); setValidationError(null); }}
                className="w-full rounded-lg border border-border/50 bg-background px-4 py-3 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary/20 min-h-[70px] resize-none"
                placeholder="Zweite Handlungsrichtung…"
              />
              {dualText.b.trim().length >= 10 && (
                <p className="text-[10px] text-primary/60 mt-1 flex items-center gap-1">
                  <Check className="h-2.5 w-2.5" /> Vorschlag vorhanden
                </p>
              )}
            </div>
            <Button
              disabled={dualText.a.trim().length < 10 || dualText.b.trim().length < 10}
              onClick={() => handleNext()}
              className="gap-1.5"
            >
              {getCtaLabel(currentQ.fieldName)} <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}

        {(currentQ.inputType === 'text' || currentQ.inputType === 'textarea') && (
          <div className="space-y-3">
            <textarea
              value={String(answers[currentQ.fieldName] ?? '')}
              onChange={(e) => { setAnswers(prev => ({ ...prev, [currentQ.fieldName]: e.target.value })); setValidationError(null); }}
              className="w-full rounded-lg border border-border/50 bg-background px-4 py-3 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary/20 min-h-[80px] resize-none"
              placeholder="Deine Antwort…"
            />
            <Button
              disabled={!answers[currentQ.fieldName] || String(answers[currentQ.fieldName]).trim().length < 10}
              onClick={() => handleNext()}
              className="gap-1.5"
            >
              {getCtaLabel(currentQ.fieldName)} <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}

        {currentQ.inputType === 'slider' && (
          <div className="space-y-5 max-w-md">
            <div className="text-center space-y-1">
              <span className="text-5xl font-bold text-foreground tabular-nums">{currentSliderValue}</span>
              <span className="text-sm text-muted-foreground/40 ml-1">/10</span>
              <p className={cn(
                "text-sm font-medium transition-all duration-300 mt-1",
                currentSliderValue <= 4 ? 'text-muted-foreground/60' :
                currentSliderValue <= 7 ? 'text-orange-500' : 'text-destructive'
              )}>
                {currentQ.fieldName === 'urgencyScore' ? getUrgencyLabel(currentSliderValue) : getImpactLabel(currentSliderValue)}
              </p>
            </div>
            <Slider
              value={[currentSliderValue]}
              onValueChange={([v]) => { setAnswers(prev => ({ ...prev, [currentQ.fieldName]: v })); setValidationError(null); }}
              min={1}
              max={10}
              step={1}
            />
            <div className="flex items-center justify-between text-[10px] text-muted-foreground/30">
              <span>Kaum spürbar</span>
              <span>Existenziell</span>
            </div>
            <Button onClick={() => handleNext(currentSliderValue)} className="gap-1.5">
              {getCtaLabel(currentQ.fieldName)} <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}

        {currentQ.inputType === 'select' && currentQ.options && (
          <div className="space-y-2">
            {currentQ.options.map((opt) => (
              <button
                key={opt.value}
                onClick={() => handleSelectAnswer(opt.value)}
                className={cn(
                  'w-full px-4 py-3 rounded-lg border text-sm text-left transition-all flex items-center justify-between gap-3',
                  answers[currentQ.fieldName] === opt.value
                    ? 'border-primary bg-primary/5 text-foreground font-medium'
                    : 'border-border/40 bg-background text-muted-foreground hover:border-primary/30 hover:text-foreground',
                )}
              >
                <span>{opt.label}</span>
                {opt.description && (
                  <span className="text-xs text-muted-foreground/50 hidden sm:inline">{opt.description}</span>
                )}
              </button>
            ))}
          </div>
        )}

        {validationError && (
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            {validationError}
          </div>
        )}
      </div>

      <div className="pt-6 border-t border-border/20">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1.5 text-muted-foreground/50 hover:text-muted-foreground">
          <ArrowLeft className="h-3.5 w-3.5" />
          Zurück zur Übersicht
        </Button>
      </div>
    </div>
  );
};
